import { exportClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  EnrichmentValidationProductFilter,
  EnrichmentValidationProductSort,
} from '../types/enrichment-validation.api.types';

import type { ValidationConnectorCode } from './enrichment-validation.service';

/** export-service pages the products read server-side, so it can outlast the default Ky
 *  timeout — same budget as the insight-membership export. */
const EXPORT_TIMEOUT_MS = 120_000;

/** Product cap export-service enforces (`DRAWER_EXPORT_MAX_ROWS`). Mirrored only to disable the
 *  button up front; the server's 413 stays authoritative. */
export const VALIDATION_EXPORT_MAX_PRODUCTS = 5_000;

/** The drawer's current filters — the same params its list query sends, minus paging. */
export interface ValidationExportParams {
  workflowId: string;
  connectorCode: ValidationConnectorCode;
  status: EnrichmentValidationProductFilter;
  q: string | null;
  rule: string | null;
  field: string | null;
  sort: EnrichmentValidationProductSort;
}

/** What was saved: the filename and the server's product count (`null` if not sent). */
export interface ValidationExportResult {
  filename: string;
  productCount: number | null;
}

/** Thrown for a 413 — the list is over the export cap. Carries the numbers for the toast. */
export class ValidationExportTooLargeError extends Error {
  readonly totalElements: number | null;
  readonly maxRows: number;

  constructor(totalElements: number | null, maxRows: number) {
    super('Validated-products export exceeds the row cap');
    this.name = 'ValidationExportTooLargeError';
    this.totalElements = totalElements;
    this.maxRows = maxRows;
  }
}

/** Reads `{ totalElements, maxRows }` off a 413 body; the defaults when it isn't JSON. */
async function readTooLargeBody(
  error: ApiError,
): Promise<{ totalElements: number | null; maxRows: number }> {
  try {
    const body = (await error.response.clone().json()) as Record<string, unknown>;
    return {
      totalElements: typeof body.totalElements === 'number' ? body.totalElements : null,
      maxRows: typeof body.maxRows === 'number' ? body.maxRows : VALIDATION_EXPORT_MAX_PRODUCTS,
    };
  } catch {
    return { totalElements: null, maxRows: VALIDATION_EXPORT_MAX_PRODUCTS };
  }
}

/**
 * Downloads every product the drawer is filtered to as CSV, one row per normalised field with
 * its before and after. Goes through `exportClient` so the bearer + `X-Subsidiary-ID`
 * interceptors run, then saves the blob. A 413 is rethrown as `ValidationExportTooLargeError`;
 * every other failure surfaces as the interceptor's `ApiError`.
 */
export async function downloadValidationExport(
  params: ValidationExportParams,
): Promise<ValidationExportResult> {
  const { workflowId, connectorCode, status, q, rule, field, sort } = params;
  // Unset filters are omitted, as in the list query: the server pattern-checks them.
  const searchParams: Record<string, string> = {
    connectorCode,
    status,
    sort,
    format: 'csv',
  };
  if (q) searchParams.q = q;
  if (rule) searchParams.rule = rule;
  if (field) searchParams.field = field;

  let response: Awaited<ReturnType<typeof exportClient.get>>;
  try {
    response = await exportClient.get(endpoints.enrichmentValidationExport.products(workflowId), {
      searchParams,
      timeout: EXPORT_TIMEOUT_MS,
    });
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 413) {
      const { totalElements, maxRows } = await readTooLargeBody(error);
      throw new ValidationExportTooLargeError(totalElements, maxRows);
    }
    throw error;
  }

  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `validated-products_${workflowId}_${status}.csv`;
  triggerBrowserDownload(blob, filename);

  const count = response.headers.get('x-export-product-count');
  const productCount = count === null ? null : Number(count);
  return { filename, productCount: Number.isFinite(productCount) ? productCount : null };
}
